import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authClient, useAuth } from "../lib/auth";
import { fetchTerms } from "../lib/api";
import { Loader2, Save, RefreshCw } from "lucide-react";
import TrustConfigSection from "./admin/TrustConfigSection";
import TrustAutomationSection from "./admin/TrustAutomationSection";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [scrape, setScrape] = useState(null);
  const [whatsapp, setWhatsapp] = useState({ base: 120, clicks_this_month: 0, displayed: 120 });
  const [terms, setTerms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [baseInput, setBaseInput] = useState(120);

  const refreshDashboard = useCallback(async () => {
    const { data } = await authClient.get("/admin/dashboard");
    setStats(data);
    setScrape(data.scrape);
    if (data.whatsapp) {
      setWhatsapp(data.whatsapp);
      setBaseInput(data.whatsapp.base);
    }
  }, []);

  useEffect(() => {
    refreshDashboard();
    fetchTerms().then(setTerms);
  }, [refreshDashboard]);

  // Poll scrape state every 3s while running
  useEffect(() => {
    if (scrape?.status !== "running") return;
    const id = setInterval(async () => {
      try {
        const { data } = await authClient.get("/admin/scrape/status");
        setScrape(data);
        if (data.status !== "running") {
          await refreshDashboard();
          fetchTerms().then(setTerms);
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(id);
  }, [scrape?.status, refreshDashboard]);

  const startScrape = async () => {
    setScraping(true);
    try {
      await authClient.post("/admin/scrape/start");
      await refreshDashboard();
    } finally {
      setScraping(false);
    }
  };

  const saveBase = async () => {
    const { data } = await authClient.put("/admin/stats/whatsapp", { base: parseInt(baseInput, 10) || 0 });
    setWhatsapp(data);
  };

  const select = (slug) => {
    const t = terms.find((x) => x.slug === slug);
    setSelected(slug);
    setDraft({
      short_definition: t.short_definition || "",
      long_definition: t.long_definition || "",
      benefits: (t.benefits || []).join(", "),
      usage: t.usage || "",
      key_ingredients: (t.key_ingredients || []).join(", "),
      concerns: (t.concerns || []).join(", "),
    });
  };

  const saveTerm = async () => {
    setSaving(true);
    try {
      const payload = {
        short_definition: draft.short_definition,
        long_definition: draft.long_definition,
        benefits: draft.benefits.split(",").map((s) => s.trim()).filter(Boolean),
        usage: draft.usage,
        key_ingredients: draft.key_ingredients.split(",").map((s) => s.trim()).filter(Boolean),
        concerns: draft.concerns.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await authClient.put(`/admin/terms/${selected}`, payload);
      const next = await fetchTerms();
      setTerms(next);
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await authClient.post(`/terms/${selected}/regenerate`);
      const next = await fetchTerms();
      setTerms(next);
      const t = next.find((x) => x.slug === selected);
      if (t) {
        setDraft({
          short_definition: t.short_definition || "",
          long_definition: t.long_definition || "",
          benefits: (t.benefits || []).join(", "),
          usage: t.usage || "",
          key_ingredients: (t.key_ingredients || []).join(", "),
          concerns: (t.concerns || []).join(", "),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between border-b border-bl-border pb-6 mb-10">
        <div>
          <span className="overline">Espace admin</span>
          <h1 className="font-serif text-3xl md:text-4xl mt-2">Bonjour {user?.name || user?.email}</h1>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="text-sm tracking-wide underline-grow"
          data-testid="admin-logout"
        >
          Déconnexion
        </button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-6 mb-12">
        <Stat label="Catégories" value={stats?.categories ?? "—"} testid="stat-categories" />
        <Stat label="Termes glossaire" value={stats?.terms ?? "—"} testid="stat-terms" />
        <Stat label="Produits" value={stats?.products ?? "—"} testid="stat-products" />
      </div>

      {/* Scrape */}
      <section className="mb-16 border border-bl-border p-8" data-testid="admin-scrape">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="overline">Scraping catalogue</span>
            <h2 className="font-serif text-2xl mt-1">Synchroniser le catalogue Beckyliss</h2>
          </div>
          <button
            onClick={startScrape}
            disabled={scraping || scrape?.status === "running"}
            className="bg-bl-text text-bl-bg px-6 py-3 text-sm tracking-wide hover:bg-bl-dore transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="scrape-start-btn"
          >
            {scrape?.status === "running" ? (<><Loader2 size={16} className="animate-spin" /> En cours…</>) : "Lancer le scraping"}
          </button>
        </div>
        {scrape && scrape.status !== "idle" && (
          <div className="mt-4">
            <p className="text-sm text-bl-textSoft mb-2" data-testid="scrape-progress">
              {scrape.status === "done" ? "Terminé" : "En cours"} — {scrape.processed || 0} / {scrape.total || 0} produits ·
              {' '}{scrape.inserted || 0} ajoutés · {scrape.updated || 0} mis à jour · {scrape.errors || 0} erreurs
            </p>
            <div className="h-1 bg-bl-soft border border-bl-border">
              <div
                className="h-full bg-bl-dore transition-all"
                style={{ width: `${scrape.total ? Math.round(((scrape.processed || 0) / scrape.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}
      </section>

      {/* Trust Score (Badge de Confiance) */}
      <TrustAdminSection />

      {/* Trust config editor */}
      <TrustConfigSection />

      {/* Trust automation (schedule + history + csv import) */}
      <TrustAutomationSection />

      {/* WhatsApp counter */}
      <section className="mb-16 border border-bl-border p-8" data-testid="admin-whatsapp">
        <span className="overline">Compteur WhatsApp</span>
        <h2 className="font-serif text-2xl mt-1 mb-6">Conseils délivrés ce mois</h2>
        <div className="grid sm:grid-cols-3 gap-6 items-end">
          <div>
            <p className="overline mb-2">Affiché</p>
            <p className="font-serif text-4xl" data-testid="whatsapp-displayed">{whatsapp.displayed}</p>
            <p className="text-xs text-bl-textSoft mt-1">
              base {whatsapp.base} + {whatsapp.clicks_this_month} clics réels
            </p>
          </div>
          <div className="sm:col-span-2 flex items-end gap-4">
            <label className="flex-1">
              <span className="overline block mb-2">Base personnalisée</span>
              <input
                type="number"
                min="0"
                value={baseInput}
                onChange={(e) => setBaseInput(e.target.value)}
                className="w-full bg-transparent border-b border-bl-border focus:border-bl-dore outline-none py-2"
                data-testid="whatsapp-base-input"
              />
            </label>
            <button
              onClick={saveBase}
              className="bg-bl-text text-bl-bg px-5 py-3 text-sm tracking-wide hover:bg-bl-dore transition-colors inline-flex items-center gap-2"
              data-testid="whatsapp-save-btn"
            >
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </div>
      </section>

      {/* Term editor */}
      <section className="grid lg:grid-cols-12 gap-8 mb-16" data-testid="admin-terms">
        <aside className="lg:col-span-4">
          <span className="overline">Termes du glossaire</span>
          <h2 className="font-serif text-2xl mt-1 mb-4">Éditer une définition</h2>
          <div className="border border-bl-border max-h-[480px] overflow-y-auto">
            {terms.map((t) => (
              <button
                key={t.slug}
                onClick={() => select(t.slug)}
                className={`w-full text-left px-4 py-3 border-b border-bl-border text-sm hover:bg-bl-soft transition-colors ${selected === t.slug ? "bg-bl-rose/40" : ""}`}
                data-testid={`admin-term-${t.slug}`}
              >
                <span className="block font-serif text-base">{t.name}</span>
                <span className="text-xs overline text-bl-textSoft">{t.category_slug}</span>
              </button>
            ))}
          </div>
        </aside>
        <div className="lg:col-span-8">
          {!draft ? (
            <p className="text-bl-textSoft text-sm">Sélectionnez un terme pour l'éditer.</p>
          ) : (
            <div className="space-y-5">
              <Field label="Définition courte" testid="edit-short">
                <textarea
                  rows={2}
                  value={draft.short_definition}
                  onChange={(e) => setDraft({ ...draft, short_definition: e.target.value })}
                  className="w-full bg-transparent border border-bl-border focus:border-bl-dore outline-none p-3 text-sm"
                  data-testid="edit-short-input"
                />
              </Field>
              <Field label="Définition longue" testid="edit-long">
                <textarea
                  rows={6}
                  value={draft.long_definition}
                  onChange={(e) => setDraft({ ...draft, long_definition: e.target.value })}
                  className="w-full bg-transparent border border-bl-border focus:border-bl-dore outline-none p-3 text-sm"
                  data-testid="edit-long-input"
                />
              </Field>
              <Field label="Bénéfices (séparés par des virgules)">
                <input
                  value={draft.benefits}
                  onChange={(e) => setDraft({ ...draft, benefits: e.target.value })}
                  className="w-full bg-transparent border border-bl-border focus:border-bl-dore outline-none p-3 text-sm"
                  data-testid="edit-benefits-input"
                />
              </Field>
              <Field label="Mode d'utilisation">
                <input
                  value={draft.usage}
                  onChange={(e) => setDraft({ ...draft, usage: e.target.value })}
                  className="w-full bg-transparent border border-bl-border focus:border-bl-dore outline-none p-3 text-sm"
                  data-testid="edit-usage-input"
                />
              </Field>
              <Field label="Ingrédients clés">
                <input
                  value={draft.key_ingredients}
                  onChange={(e) => setDraft({ ...draft, key_ingredients: e.target.value })}
                  className="w-full bg-transparent border border-bl-border focus:border-bl-dore outline-none p-3 text-sm"
                  data-testid="edit-ingredients-input"
                />
              </Field>
              <Field label="Préoccupations">
                <input
                  value={draft.concerns}
                  onChange={(e) => setDraft({ ...draft, concerns: e.target.value })}
                  className="w-full bg-transparent border border-bl-border focus:border-bl-dore outline-none p-3 text-sm"
                  data-testid="edit-concerns-input"
                />
              </Field>
              <div className="flex gap-3">
                <button
                  onClick={saveTerm}
                  disabled={saving}
                  className="bg-bl-text text-bl-bg px-6 py-3 text-sm hover:bg-bl-dore disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="edit-save-btn"
                >
                  <Save size={14} /> Enregistrer
                </button>
                <button
                  onClick={regenerate}
                  disabled={saving}
                  className="border border-bl-text px-6 py-3 text-sm hover:bg-bl-text hover:text-bl-bg disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="edit-regenerate-btn"
                >
                  <RefreshCw size={14} /> Régénérer (IA)
                </button>
                <Link
                  to={`/terme/${selected}`}
                  target="_blank"
                  className="ml-auto text-sm underline-grow"
                  data-testid="edit-preview-link"
                >
                  Voir la fiche →
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="border border-bl-border p-6" data-testid={testid}>
    <p className="overline">{label}</p>
    <p className="font-serif text-4xl mt-2">{value}</p>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline block mb-2">{label}</span>
    {children}
  </label>
);


function TrustAdminSection() {
  const [state, setState] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authClient.get("/admin/trust/status");
      setState(data);
    } catch { /* ignore */ }
    try {
      const { data } = await authClient.get("/admin/trust/report");
      setReport(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (state?.status !== "running") return;
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [state?.status, refresh]);

  const start = async () => {
    setBusy(true);
    try {
      await authClient.post("/admin/trust/batch");
      await refresh();
    } finally { setBusy(false); }
  };

  const pct = state?.total ? Math.round(((state.processed || 0) / state.total) * 100) : 0;

  return (
    <section className="mb-16 border border-bl-border p-8" data-testid="admin-trust">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="overline">Badge de confiance</span>
          <h2 className="font-serif text-2xl mt-1">Scoring de confiance des produits</h2>
          <p className="text-sm text-bl-textSoft mt-2">
            Analyse INCI + qualités du catalogue Wix ; score sur 10 par produit, 3 grilles (soins / maquillage / compléments).
          </p>
        </div>
        <button
          onClick={start}
          disabled={busy || state?.status === "running"}
          className="bg-bl-text text-bl-bg px-6 py-3 text-sm tracking-wide hover:bg-bl-dore transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="trust-batch-btn"
        >
          {state?.status === "running" ? (<><Loader2 size={16} className="animate-spin" /> En cours…</>) : "Lancer le scoring"}
        </button>
      </div>
      {state && state.status !== "idle" && (
        <div className="mt-4">
          <p className="text-sm text-bl-textSoft mb-2" data-testid="trust-progress">
            {state.status === "done" ? "Terminé" : "En cours"} — {state.processed || 0} / {state.total || 0} produits ·
            {' '}{state.scored || 0} notés · {state.excluded || 0} exclus · {state.errors || 0} erreurs
          </p>
          <div className="h-1 bg-bl-soft border border-bl-border">
            <div className="h-full bg-bl-dore transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      {report && report.total_scored > 0 && (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="border border-bl-border p-4">
            <span className="overline">Total noté</span>
            <p className="font-serif text-3xl mt-1">{report.total_scored}</p>
          </div>
          <div className="border border-bl-border p-4">
            <span className="overline">Excellente ≥ 9</span>
            <p className="font-serif text-3xl mt-1">{report.distribution?.excellent || 0}</p>
          </div>
          <div className="border border-bl-border p-4">
            <span className="overline">Très bonne ≥ 7,5</span>
            <p className="font-serif text-3xl mt-1">{report.distribution?.tres_bonne || 0}</p>
          </div>
          <div className="border border-bl-border p-4">
            <span className="overline">Couverture INCI</span>
            <p className="font-serif text-3xl mt-1">{Math.round((report.inci_coverage?.ratio || 0) * 100)}%</p>
          </div>
        </div>
      )}
    </section>
  );
}
